/**
 * filterLogic — the pure rules behind the DS Filter.
 *
 * A filter VALUE is a plain object keyed by attribute id:
 *   options attribute → array of option values  { color: ['black', 'red'] }
 *   range attribute   → [min, max]              { price: [200, 600] }
 * Nothing else is ever stored: an empty selection and a range equal to the
 * attribute's bounds are removed, so `{}` always means "no filters".
 *
 * Attributes are data (the shape a CMS sends):
 *   { id, label, type: 'options', multiple?, field?, options: [{ value, label, swatch?, count?, disabled? }] }
 *   { id, label, type: 'range', min, max, step?, unit?, field? }
 * `field` names the item key the attribute reads (default: the id). An item
 * field may hold one value or an array of values (a product in two colours).
 *
 * Every function returns a NEW value — the inputs are never mutated.
 */

const isNumber = (n) => typeof n === 'number' && Number.isFinite(n);

const fieldOf = (attribute) => attribute.field || attribute.id;

const optionValues = (attribute) => (attribute.options || []).map((o) => o.value);

/** Whether a stored range narrows the attribute at all. */
export const isRangeActive = (attribute, range) =>
  Array.isArray(range) &&
  range.length === 2 &&
  isNumber(range[0]) &&
  isNumber(range[1]) &&
  (range[0] > attribute.min || range[1] < attribute.max);

/** Clamp a range into the attribute's bounds, smaller value first. */
export const clampRange = (attribute, range) => {
  const [a, b] = range;
  const lo = Math.min(a, b);
  const hi = Math.max(a, b);
  return [Math.max(attribute.min, lo), Math.min(attribute.max, hi)];
};

const normalizeOne = (attribute, raw) => {
  if (attribute.type === 'range') {
    if (!Array.isArray(raw) || !isNumber(raw[0]) || !isNumber(raw[1])) return undefined;
    const range = clampRange(attribute, raw);
    return isRangeActive(attribute, range) ? range : undefined;
  }
  if (!Array.isArray(raw)) return undefined;
  const known = new Set(optionValues(attribute));
  const picked = raw.filter((v, i) => known.has(v) && raw.indexOf(v) === i);
  const kept = attribute.multiple === false ? picked.slice(0, 1) : picked;
  return kept.length ? kept : undefined;
};

/** Drop everything the attributes cannot hold; clamp and order ranges. */
export const normalizeValue = (attributes, value = {}) => {
  const next = {};
  attributes.forEach((attribute) => {
    const v = normalizeOne(attribute, value[attribute.id]);
    if (v !== undefined) next[attribute.id] = v;
  });
  return next;
};

/** How many choices an attribute carries: options picked, or 1 for an active range. */
export const countForAttribute = (attribute, attrValue) => {
  if (attribute.type === 'range') return isRangeActive(attribute, attrValue) ? 1 : 0;
  return Array.isArray(attrValue) ? attrValue.length : 0;
};

/** Total number of active choices — the count on the mobile "Filters" button. */
export const countActive = (attributes, value = {}) =>
  attributes.reduce((sum, attribute) => sum + countForAttribute(attribute, value[attribute.id]), 0);

const withAttribute = (value, id, attrValue) => {
  const next = { ...value };
  if (attrValue === undefined) delete next[id];
  else next[id] = attrValue;
  return next;
};

/**
 * Toggle one option. Multi-choice attributes add / remove it; a single-choice
 * attribute (`multiple: false`) switches to it, and picking the current one
 * clears it.
 */
export const toggleOption = (value, attribute, optionValue) => {
  const current = Array.isArray(value[attribute.id]) ? value[attribute.id] : [];
  let picked;
  if (attribute.multiple === false) {
    picked = current[0] === optionValue ? [] : [optionValue];
  } else {
    picked = current.includes(optionValue)
      ? current.filter((v) => v !== optionValue)
      : [...current, optionValue];
  }
  return withAttribute(value, attribute.id, picked.length ? picked : undefined);
};

/** Store a range (clamped, ordered); one equal to the bounds removes the filter. */
export const setRange = (value, attribute, range) =>
  withAttribute(value, attribute.id, normalizeOne(attribute, range));

export const clearAttribute = (value, id) => withAttribute(value, id, undefined);

const defaultFormatRange = ([a, b], attribute) =>
  `${a}–${b}${attribute.unit ? ` ${attribute.unit}` : ''}`;

/**
 * The removable chips under the bar, in attribute order; options in the order
 * they were picked. A range chip has `value: null`.
 */
export const activeChips = (attributes, value = {}, { formatRange = defaultFormatRange } = {}) =>
  attributes.flatMap((attribute) => {
    const attrValue = value[attribute.id];
    if (attribute.type === 'range') {
      return isRangeActive(attribute, attrValue)
        ? [{
          key: `${attribute.id}:range`,
          attributeId: attribute.id,
          attributeLabel: attribute.label,
          value: null,
          label: formatRange(attrValue, attribute),
        }]
        : [];
    }
    if (!Array.isArray(attrValue)) return [];
    const byValue = new Map((attribute.options || []).map((o) => [o.value, o]));
    return attrValue
      .filter((v) => byValue.has(v))
      .map((v) => ({
        key: `${attribute.id}:${v}`,
        attributeId: attribute.id,
        attributeLabel: attribute.label,
        value: v,
        label: byValue.get(v).label,
      }));
  });

export const removeChip = (value, chip) => {
  if (chip.value === null) return clearAttribute(value, chip.attributeId);
  const rest = (value[chip.attributeId] || []).filter((v) => v !== chip.value);
  return withAttribute(value, chip.attributeId, rest.length ? rest : undefined);
};

const asList = (fieldValue) => {
  if (fieldValue === undefined || fieldValue === null) return [];
  return Array.isArray(fieldValue) ? fieldValue : [fieldValue];
};

const matchesAttribute = (item, attribute, attrValue) => {
  const fieldValue = item[fieldOf(attribute)];
  if (attribute.type === 'range') {
    if (!isRangeActive(attribute, attrValue)) return true;
    return isNumber(fieldValue) && fieldValue >= attrValue[0] && fieldValue <= attrValue[1];
  }
  if (!Array.isArray(attrValue) || attrValue.length === 0) return true;
  return asList(fieldValue).some((v) => attrValue.includes(v));
};

const matchesAll = (item, attributes, value, skipId) =>
  attributes.every((attribute) =>
    attribute.id === skipId || matchesAttribute(item, attribute, value[attribute.id]));

/** Items that pass every attribute: options OR within an attribute, AND across attributes. */
export const applyFilters = (items, attributes, value = {}) =>
  items.filter((item) => matchesAll(item, attributes, value));

/**
 * Per-option result counts for every options attribute, each computed with the
 * OTHER attributes' filters applied — so a count says what picking that option
 * would show. Feed it to the Filter's `getCounts`.
 */
export const facetCounts = (items, attributes, value = {}) => {
  const counts = {};
  attributes.forEach((attribute) => {
    if (attribute.type === 'range') return;
    const pool = items.filter((item) => matchesAll(item, attributes, value, attribute.id));
    const perOption = {};
    optionValues(attribute).forEach((v) => { perOption[v] = 0; });
    pool.forEach((item) => {
      asList(item[fieldOf(attribute)]).forEach((v) => {
        if (v in perOption) perOption[v] += 1;
      });
    });
    counts[attribute.id] = perOption;
  });
  return counts;
};

/**
 * Sort with the comparator registered for `sort` (`sorters` = { [sortValue]: (a, b) => number }).
 * An unknown or empty sort keeps the incoming order (the "recommended" order).
 */
export const sortItems = (items, sort, sorters = {}) => {
  const compare = sort ? sorters[sort] : null;
  return typeof compare === 'function' ? [...items].sort(compare) : [...items];
};
